import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { formatCentsToCurrency } from '@frontend/currency';
import { SendInThreadModalContents } from '@frontend/integrated-messaging';
import { getPaySiteInvoiceUrl } from '@frontend/payments-pay-site-url';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { ContentLoader } from '@frontend/design-system';
import { useValidPhoneNumberFromInvoice } from '../../../../hooks/use-valid-phone-number-from-invoice';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useBnplLink } from '../../../hooks/use-bnpl-link';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { ShareInMessageBodyProps } from './share-in-message.types';

const styles = {
  loader: css`
    height: 200px;
  `,
  body: css`
    .wv-modal-footer {
      @media screen and (min-width: ${breakpoints.mobile.max}px) {
        padding: 0;
      }
    }
  `,
};

export const ShareInMessageBody = ({
  isBnplRequest = false,
  step = PAYMENT_MULTI_STEPS.shareInMessage,
  onGoBack,
  onCompleteStep,
}: ShareInMessageBodyProps) => {
  const { invoice, setMinWidth, resetMinWidth } = useCollectPaymentMultiContext();
  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);
  const isSmallMode = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const [selectedPhone, setSelectedPhone] = useState<string>(firstValidPhoneNumber ?? '');
  const { bnplTtpLink, bnplLinkLoading } = useBnplLink({ personPhone: selectedPhone, isBnplRequest });

  useEffect(() => {
    setMinWidth(800);
    return () => {
      resetMinWidth();
    };
  }, [setMinWidth, resetMinWidth]);

  const onSend = () => {
    setTimeout(() => onCompleteStep(), 2000);
  };

  if (!invoice) return null;

  if (bnplLinkLoading && isBnplRequest)
    return (
      <section css={styles.loader}>
        <ContentLoader show={true} />
      </section>
    );

  return (
    <StyledStep
      id={step}
      label={PAYMENT_MULTI_STEP_LABELS[step]}
      hasInternalFooter
      components={{
        Footer: () => null,
      }}
    >
      <SendInThreadModalContents
        groupId={invoice.locationId}
        onBack={onGoBack}
        personId={invoice.person.id || ''}
        initPersonPhone={firstValidPhoneNumber}
        templateType={TemplateType_Slug.MANUAL_PAYMENT_REQUEST}
        linkData={{
          link: isBnplRequest && !!bnplTtpLink ? bnplTtpLink : getPaySiteInvoiceUrl(invoice?.links?.payment) ?? '',
          relatedId: invoice.id,
        }}
        onThreadChange={({ personPhone }) => setSelectedPhone(personPhone)}
        {...(isSmallMode && { smallModeThreadBodyMinHeight: 0 })}
        onSchedule={onCompleteStep}
        onSend={onSend}
        onClose={onCompleteStep}
        hideHeader={true}
        contextPreviewProps={{
          iconName: 'pay',
          title: formatCentsToCurrency(invoice?.billedAmount),
          subtitle: formatCentsToCurrency(invoice?.billedAmount),
        }}
      />
    </StyledStep>
  );
};
